import {
  AccountStatus,
  AccountType,
  AccountUserType,
  DateFormat,
  UserType,
} from '__generated__/atlas-types'

import { useViewingAccountUserQuery } from 'hooks/useViewingAccountInfo'

import {
  FORMAT_DD_MM_YYYY,
  FORMAT_MM_DD_YYYY,
  FORMAT_YYYY_MM_DD,
} from './dates'
import { fromGlobalId } from './transformations/graphql'

type CurrentAccountInfo = {
  accountId: number | null
  profileName: string
  logoURL: string
  type: number | null
  status: AccountStatus | null
  canTransact: boolean
  canViewDataPortal: boolean
  limitedAccess: boolean
  coreBrand: boolean
  canViewAssortments: boolean
  enableChat: boolean
  canShop: boolean
  canFindConnections: boolean
  origin: string
  subtype: string
}

// TODO:
// let's add support for more undefined or null here
export type CurrentUserInfo = {
  apiHost: string
  loggedAsAdmin: boolean
  isAdmin: boolean
  isOwner: boolean
  account: CurrentAccountInfo
  assortmentPlanning: boolean
  canViewRetailerSettings: boolean
  // changing this to an array of strings. i'm assuming right now the string type is a mistake
  integrations: string[]
  isTiedWithBuyerHierarchy: boolean
  settings: any
  id: string
  // im assuming again this is a string by mistake. it should be an int
  userId: number | null
  // same, assuming this was a string by mistake
  accountUserId: number | null
  accountUserType: AccountUserType | null
  accounts?: { id: string; name?: string | null }[]
  isInternal: boolean
  isLoggedIn: boolean
  name: string
  dateFormat: string
  email: string
  userType: UserType
  lastLogin: string
  emailLanguage: string
  displayLanguage: string
}

const mapTypeToNumber = (accountType: AccountType | null | undefined) => {
  if (accountType) {
    return {
      ADMIN: 1,
      BRAND: 2,
      RETAILER: 3,
    }[accountType]
  }

  return null
}

const mapEnumToDateFormat = (dateFormat: DateFormat | null | undefined) => {
  if (dateFormat) {
    return {
      MONTH_FIRST: FORMAT_MM_DD_YYYY,
      DAY_FIRST: FORMAT_DD_MM_YYYY,
      YEAR_FIRST: FORMAT_YYYY_MM_DD,
    }[dateFormat]
  }
  return null
}

export const getUserInfo = (
  viewingUser: ReturnType<
    typeof useViewingAccountUserQuery
  >['userDataFromQuery'],
): CurrentUserInfo => {
  return {
    apiHost: viewingUser.apiHost,
    loggedAsAdmin: viewingUser.loggedAsAdmin ?? false,
    isAdmin: viewingUser.isAdmin ?? false,
    isOwner: viewingUser.isOwner ?? false,
    account: {
      accountId: viewingUser?.account?.id
        ? parseInt(fromGlobalId(viewingUser?.account?.id).id!, 10)
        : null,
      profileName: viewingUser?.account?.profileName ?? '',
      logoURL: viewingUser?.account?.logoUrl ?? '',
      type: mapTypeToNumber(viewingUser?.account?.type) ?? null,
      status: viewingUser?.account?.status || null,
      canTransact: viewingUser?.account?.canTransact ?? false,
      canViewDataPortal:
        (viewingUser?.isAdmin || viewingUser?.account?.canViewDataPortal) ??
        false,
      limitedAccess: viewingUser?.account?.limitedAccess ?? false,
      coreBrand: viewingUser?.account?.coreBrand ?? false,
      enableChat: viewingUser?.account?.enableChat ?? true,
      canViewAssortments: viewingUser?.account?.canViewAssortments ?? false,
      canShop: viewingUser?.account?.canShop ?? false,
      canFindConnections: viewingUser?.account?.canFindBrands ?? false,
      origin: viewingUser?.account?.origin ?? '',
      subtype: viewingUser?.account?.subtype ?? '',
    },
    id: viewingUser?.id ?? '',
    userId: viewingUser?.id
      ? parseInt(fromGlobalId(viewingUser.id).id!, 10)
      : null,
    accountUserId: viewingUser?.accountUserId
      ? parseInt(fromGlobalId(viewingUser.accountUserId).id!, 10)
      : null,
    accountUserType:
      viewingUser?.accountUserType ?? AccountUserType.UNSPECIFIED,
    accounts: viewingUser?.accounts ?? [],
    assortmentPlanning: viewingUser?.assortmentPlanning || false,
    canViewRetailerSettings: viewingUser?.userCanViewRetailerSettings || false,
    integrations: viewingUser?.integrations || [],
    isTiedWithBuyerHierarchy: viewingUser?.tiedWithBuyerHierarchy ?? false,
    settings: viewingUser?.settings ?? null,
    isInternal: viewingUser?.isInternal ?? false,
    isLoggedIn: viewingUser?.isLoggedIn ?? false,
    name: viewingUser?.name ?? '',
    dateFormat: mapEnumToDateFormat(viewingUser?.dateFormat) ?? '',
    email: viewingUser?.email ?? '',
    userType: viewingUser?.userType ?? UserType.UNSPECIFIED,
    lastLogin: viewingUser?.lastLogin ?? '',
    emailLanguage: viewingUser?.emailLanguage ?? '',
    displayLanguage: viewingUser?.displayLanguage ?? '',
  }
}

export const getLaunchDarklyUserInfo = (userInfo: any) => {
  const user = Boolean(userInfo.userId)
  const account = Boolean(userInfo.account.accountId)
  const name = (account && userInfo.account.profileName) || 'anonymous'
  return {
    key: (account && userInfo.account.accountId) || 'anonymous',
    name,
    ...(!account && { anonymous: true }),
    userId: user && userInfo.userId,
    accountId: account && userInfo.account.accountId,
    accountUserId: user && userInfo.accountUserId,
    isAdmin: user && userInfo.isAdmin,
    loggedAsAdmin: user && userInfo.loggedAsAdmin,
    accountProfileName: name,
    accountType: account && userInfo.account.type,
    isInternal: user && userInfo.isInternal,
    isLimitedAccess: account && userInfo.account.limitedAccess,
    isCoreBrand: account && userInfo.account.coreBrand,
    host: window.location.href,
  }
}
